import { MouseEvent, useState } from 'react';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, Checkbox, Menu, MenuItem, Stack } from '@mui/material';
import Button from 'domains/core/components/Button';
import { themeColors } from 'colors';

export type MultiSelectOption<T> = {
    value: T;
    label: string;
    count?: number;
};

export type MultiSelectDropdownProps<T> = {
    /** Array of options to display in the dropdown */
    options: MultiSelectOption<T>[];
    /** Currently selected values */
    selectedValues: T[];
    /** Callback when Apply button is clicked */
    onApply: (values: T[]) => void;
    /** Label for the dropdown button (default: "Status") */
    label?: string;
    /** Prefix for data-testid attributes (default: "multiselect") */
    testIdPrefix?: string;
    /** ID for the menu element (default: "multiselect-menu") */
    menuId?: string;
    /** Max height for the scrollable options list */
    maxHeight?: string;
    disabled?: boolean;
};

const MultiSelectDropdown = <T extends string>({
    options,
    selectedValues,
    onApply,
    label = 'Status',
    testIdPrefix = 'multiselect',
    menuId = 'multiselect-menu',
    maxHeight,
    disabled,
}: MultiSelectDropdownProps<T>) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [localSelectedValues, setLocalSelectedValues] = useState<T[]>(selectedValues);
    const isOpen = Boolean(anchorEl);

    // Opens the dropdown menu. Reset local state to the current committed selection
    // so any unsaved changes from a previous open session are discarded.
    const handleClick = (event: MouseEvent<HTMLElement>) => {
        setLocalSelectedValues(selectedValues);
        setAnchorEl(event.currentTarget);
    };

    // Closes the dropdown menu
    const handleClose = () => {
        setAnchorEl(null);
    };

    // Toggles the selection of an option (selects if unselected, deselects if already selected)
    // Updates only local state - changes are not applied until Apply button is clicked
    const handleToggleValue = (value: T) => {
        setLocalSelectedValues((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    };

    // Applies the selected filters and closes the dropdown
    const handleApply = () => {
        onApply(localSelectedValues);
        handleClose();
    };

    // Clears all selected values from local state
    // Does not apply changes - user must click Apply to update the filter
    const handleClear = () => {
        setLocalSelectedValues([]);
    };

    const hasSelectedValues = selectedValues.length > 0;

    return (
        <>
            <Button
                onClick={handleClick}
                endIcon={<KeyboardArrowDown />}
                sx={{
                    textTransform: 'capitalize',
                    color: hasSelectedValues ? themeColors.action.active : 'inherit',
                }}
                variant="text"
                disabled={disabled}
                aria-controls={isOpen ? menuId : undefined}
                aria-expanded={isOpen ? 'true' : undefined}
                aria-haspopup="menu"
                data-testid={`${testIdPrefix}-button`}
                role="button"
            >
                {hasSelectedValues ? `${label} (${selectedValues.length})` : label}
            </Button>
            <Menu
                id={menuId}
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        minWidth: '200px',
                        mt: 1,
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
                MenuListProps={{
                    role: 'menu',
                    sx: { py: 0 },
                }}
            >
                <Box
                    data-testid={`${testIdPrefix}-menu`}
                    sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                >
                    {/* Scrollable options list */}
                    <Box sx={{ overflowY: 'auto', ...(maxHeight && { maxHeight }) }}>
                        {options.map((option) => {
                            const isSelected = localSelectedValues.includes(option.value);
                            return (
                                <MenuItem
                                    key={option.value}
                                    onClick={() => handleToggleValue(option.value)}
                                    data-testid={`${testIdPrefix}-option-${option.value.toLowerCase()}`}
                                    role="menuitemcheckbox"
                                    aria-checked={isSelected}
                                    sx={{ px: 1, display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                                >
                                    <Checkbox
                                        checked={isSelected}
                                        size="small"
                                        data-testid={`${testIdPrefix}-checkbox-${option.value.toLowerCase()}`}
                                        sx={{
                                            py: 0,
                                            color: 'primary.main',
                                            '&.Mui-checked': { color: 'primary.main' },
                                            '& svg': { mt: '2px' },
                                        }}
                                        tabIndex={-1}
                                        disableRipple
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>{option.label}</Box>
                                </MenuItem>
                            );
                        })}
                    </Box>
                    {/* Sticky Clear / Apply actions */}
                    <Box
                        sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1, mt: 0.5, flexShrink: 0 }}
                        data-testid={`${testIdPrefix}-actions`}
                    >
                        <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleClear}
                                data-testid={`${testIdPrefix}-clear-button`}
                                role="button"
                                sx={{
                                    flex: 1,
                                    textTransform: 'none',
                                }}
                                disabled={localSelectedValues.length === 0}
                            >
                                Clear
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleApply}
                                data-testid={`${testIdPrefix}-apply-button`}
                                role="button"
                                sx={{
                                    flex: 1,
                                    textTransform: 'none',
                                    bgcolor: themeColors.action.active,
                                    '&:hover': {
                                        bgcolor: themeColors.action.selected,
                                    },
                                }}
                            >
                                Apply
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Menu>
        </>
    );
};

export default MultiSelectDropdown;
